// ABOUTME: Small globe badge marking machine-translated activity text.
// ABOUTME: Hover reveals the author's original; the locked variant leads to the subscription page.

import { useNavigate } from "react-router"
import { useTranslation } from "react-i18next"
import { LockIcon, TranslateIcon } from "@shared/icons"
import CTooltip from "../CTooltip"
import { SUBSCRIPTION_PATH } from "@shared/constants/navigation"
import type { L10nBadgeKind } from "./fn"

interface IProps {
  kind: L10nBadgeKind
  /** Author's text, shown in the tooltip of the "translated" badge. */
  original?: string
  size?: number
  className?: string
}

/**
 * PRO upsell for a translation the viewer's plan doesn't cover. Kept separate so
 * the router hook only runs when a lock is actually on screen — the common,
 * untranslated case must stay usable outside a Router (Storybook, unit tests).
 */
const LockedBadge = ({ size, className }: { size: number; className: string }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const openSubscription = (e: { stopPropagation: () => void }) => {
    e.stopPropagation()
    navigate(SUBSCRIPTION_PATH)
  }

  return (
    <CTooltip
      content={t("l10n.lockedHint")}
      placement="top"
    >
      <span
        role="button"
        tabIndex={0}
        aria-label={t("l10n.lockedHint")}
        onClick={openSubscription}
        onKeyDown={(e) => e.key === "Enter" && openSubscription(e)}
        className={`inline-flex shrink-0 items-center gap-[2px] align-middle cursor-pointer ${className}`}
      >
        <TranslateIcon
          size={size}
          color="#C4B5FD"
        />
        <LockIcon
          size={size * 0.75}
          color="#7C3AED"
        />
      </span>
    </CTooltip>
  )
}

/** Translation is being produced in the background — the original is on screen meanwhile. */
const PendingBadge = ({ size, className }: { size: number; className: string }) => {
  const { t } = useTranslation()

  return (
    <CTooltip
      content={t("l10n.translating")}
      placement="top"
    >
      <span
        aria-label={t("l10n.translating")}
        className={`inline-flex shrink-0 items-center align-middle ${className}`}
      >
        {/* Slower than the default 1s so it reads as "working", not "loading". */}
        <span className="inline-flex animate-spin [animation-duration:2.5s]">
          <TranslateIcon
            size={size}
            color="#C4B5FD"
          />
        </span>
      </span>
    </CTooltip>
  )
}

const TranslatedBadge = ({ original, size, className }: { original?: string; size: number; className: string }) => {
  const { t } = useTranslation()

  return (
    <CTooltip
      content={
        <span className="block max-w-xs whitespace-pre-wrap wrap-anywhere">
          {`${t("l10n.translated")}\n\n${original ?? ""}`.trim()}
        </span>
      }
      placement="top"
    >
      <span
        aria-label={t("l10n.translated")}
        className={`inline-flex shrink-0 items-center align-middle ${className}`}
      >
        <TranslateIcon size={size} />
      </span>
    </CTooltip>
  )
}

/**
 * Renders nothing for `kind === "none"` so callers can drop it in unconditionally.
 */
export const L10nBadge = ({ kind, original, size = 12, className = "" }: IProps) => {
  if (kind === "none") {
    return null
  }

  if (kind === "locked") {
    return (
      <LockedBadge
        size={size}
        className={className}
      />
    )
  }

  if (kind === "pending") {
    return (
      <PendingBadge
        size={size}
        className={className}
      />
    )
  }

  return (
    <TranslatedBadge
      original={original}
      size={size}
      className={className}
    />
  )
}

export default L10nBadge
